// Wire (./types.ts) -> view model (./model.ts). Pure functions, no I/O.
// Display-only arithmetic lives here and nowhere else: sums of USD values for
// a headline, a share for a bar width. The server remains authoritative for
// every stored and derived number; an unknown value stays `null` + a reason.
import { addDecimal, compareDecimal, formatDecimal, isZeroDecimal, subDecimal } from '../lib/decimal';
import { candidateNoteText } from '../i18n/reasons.hu';
import { daysBetween, formatUtc } from '../lib/time';
import type {
  BalanceRow,
  Balances,
  BreakdownSegment,
  Candidate,
  CandidateList,
  CandlePrice,
  ClosedLoansView,
  DealBreakdown,
  DealDetail,
  DealSummary,
  EventRow,
  EventsPage,
  FlowNodeView,
  LocationItem,
  LocationSummary,
  MarkOutcome,
  MergeSuggestion,
  Money,
  LoanLine,
  OpenLoan,
  PrehistoryCredit,
  Overview,
  PositionRow,
  Receivable,
  LoanListView,
  RepaySuggestionView,
} from './model';
import type * as W from './types';

const BINANCE_MARGIN = 'Binance Margin';

export function toEventRow(e: W.EventOut): EventRow {
  const note = e.annotation?.note?.trim();
  return {
    id: e.id,
    occurred_at: e.occurred_at,
    type: e.type,
    source: e.source,
    location: e.location ?? '—',
    counter_location: e.counter_location,
    asset: e.asset,
    quantity: e.quantity,
    quote_asset: e.quote_asset,
    price: e.price,
    price_level: e.price_level,
    price_level_asset: e.price_level_asset,
    price_level_reason: e.price_level_reason,
    price_level_source: e.price_level_source ?? null,
    fee_asset: e.fee_asset,
    fee_quantity: e.fee_quantity,
    returnable: e.returnable,
    note: note ? note : null,
    tags: [...(e.annotation?.tags ?? [])],
    voided: e.voided_at !== null,
    direction: e.direction ?? 'neutral',
    display_quantity: e.display_quantity ?? e.quantity,
    allocations: (e.allocations ?? []).map((a) => ({ deal_id: a.deal_id, deal_name: a.deal_name, quantity: a.quantity })),
    merge: e.merge ? { status: e.merge.status, counterpart_source: e.merge.counterpart_source } : null,
    interest_quantity: e.interest_quantity ?? null,
    interest_trueup_quantity: e.interest_trueup_quantity ?? null,
    repay_links: (e.repay_links ?? []).map((l) => ({ ...l })),
    repay_unassigned_quantity: e.repay_unassigned_quantity ?? null,
    repay_realized_result_usd: e.repay_realized_result_usd ?? null,
    repay_realized_result_incl_interest_usd: e.repay_realized_result_incl_interest_usd ?? null,
    repay_realized_result_reason: e.repay_realized_result_reason ?? null,
    repay_interest_usd: e.repay_interest_usd ?? null,
    prehistory: e.prehistory ? { note: e.prehistory.note, marked_at: e.prehistory.marked_at, quantity: e.prehistory.quantity } : null,
  };
}

/** True when a repay still has a part no borrow is linked to (D47: "Hozzárendelésre vár"). */
export const isUnassignedRepay = (e: Pick<EventRow, 'type' | 'repay_unassigned_quantity'>): boolean =>
  e.type === 'repay' && e.repay_unassigned_quantity !== null && compareDecimal(e.repay_unassigned_quantity, '0') > 0;

export function toEventsPage(l: W.EventList): EventsPage {
  return {
    items: l.items.map(toEventRow),
    total: l.total,
    limit: l.limit ?? l.items.length,
    offset: l.offset ?? 0,
    facets: l.facets ? { source: l.facets.source as NonNullable<EventsPage['facets']>['source'], type: l.facets.type as NonNullable<EventsPage['facets']>['type'] } : null,
  };
}

export const toPrehistoryCredits = (list: W.Amount[] | undefined): PrehistoryCredit[] =>
  (list ?? []).map((a) => ({ asset: a.asset, quantity: a.quantity, value_usd: a.value_usd ?? null, value_reason: a.value_reason ?? null }));

const money = (k: W.Kpi): Money => ({ value: k.value, reason: k.reason ?? null });

/** Sum of USD values; null + the first reason as soon as one part is unknown. */
export function sumUsd(parts: readonly { value_usd?: string | null; value_reason?: string | null }[]): Money {
  let total = '0';
  for (const p of parts) {
    if (p.value_usd === null || p.value_usd === undefined) return { value: null, reason: p.value_reason ?? 'no_price' };
    total = addDecimal(total, p.value_usd);
  }
  return { value: total, reason: null };
}

/** Level reasons that a Candle retry cannot fix: the borrow must first be allocated to a deal with a reference coin (D10). */
const NEEDS_DEAL_REASONS = new Set(['no_deal_allocation', 'deal_reference_asset_missing', 'reference_asset_is_stablecoin']);

export function toLoanLine(l: W.LoanRow): LoanLine {
  const cmp = l.repay_cost_change_usd === null ? null : compareDecimal(l.repay_cost_change_usd, '0');
  return {
    event_id: l.event_id,
    occurred_at: l.occurred_at,
    location_id: l.location_id,
    location: l.location,
    asset: l.asset,
    price_kind: l.price_kind,
    status: l.status,
    quantity: l.quantity,
    outstanding_quantity: l.outstanding_quantity,
    price_level_asset: l.price_level_asset,
    price_level: l.price_level,
    price_level_reason: l.price_level_reason,
    price_level_source: l.price_level_source,
    value_at_borrow_usd: l.value_at_borrow_usd,
    value_at_borrow_reason: l.value_at_borrow_reason,
    current_price_usd: l.current_price_usd,
    current_price_reason: l.current_price_reason,
    current_price_source: l.current_price_source ?? null,
    value_now_usd: l.value_now_usd,
    value_now_reason: l.value_now_reason,
    change_usd: l.repay_cost_change_usd,
    change_pct: l.repay_cost_change_pct,
    change_reason: l.repay_cost_change_reason,
    verdict: cmp === null ? null : cmp > 0 ? 'worse' : cmp < 0 ? 'better' : 'even',
    // A missing level can be asked for again; a manual one can be replaced by the Candle level (the server asks for confirmation first).
    can_recompute: (l.price_level === null && !NEEDS_DEAL_REASONS.has(l.price_level_reason ?? '')) || l.price_level_source === 'manual',
    deal_id: l.deal_id,
    deal_name: l.deal_name,
    realized_result_usd: l.realized_result_usd,
    realized_result_reason: l.realized_result_reason,
    realized_result_incl_interest_usd: l.realized_result_incl_interest_usd,
    realized_result_incl_interest_reason: l.realized_result_incl_interest_reason,
    linked_repays: l.linked_repays.map((r) => ({ ...r })),
    closed_at: l.closed_at,
    interest_paid_quantity: l.interest_paid_quantity,
    interest_paid_usd: l.interest_paid_usd,
    interest_paid_reason: l.interest_paid_reason,
  };
}

export function toLoanListView(l: W.LoanList): LoanListView {
  return {
    items: l.items.map(toLoanLine),
    unassigned: (l.unassigned ?? []).map((g) => ({ ...g, repays: g.repays.map((r) => ({ ...r })) })),
    prehistory: (l.prehistory ?? []).map((g) => ({ ...g, repays: g.repays.map((r) => ({ ...r })) })),
  };
}

export function toClosedLoansView(l: W.ClosedLoanList): ClosedLoansView {
  return {
    items: l.items.map((i) => ({ ...i, linked_repays: i.linked_repays.map((r) => ({ ...r })) })),
    total: l.total,
    limit: l.limit,
    offset: l.offset,
    totals: { count: l.totals.count, per_asset: l.totals.per_asset.map((a) => ({ ...a })), usd: { ...l.totals.usd } },
    prehistory: l.prehistory.map((g) => ({ ...g })),
  };
}

export function toRepaySuggestion(r: W.RepaySuggestion): RepaySuggestionView {
  return { ...r, items: r.items.map((i) => ({ ...i })) };
}

export function toOpenLoan(l: W.OpenLoanRow): OpenLoan {
  return {
    key: `${l.deal_id ?? 'own'}-${l.location_id}-${l.asset}`,
    deal_id: l.deal_id,
    deal_name: l.deal_name,
    location: l.location,
    asset: l.asset,
    opened_at: l.opened_at ?? null,
    quantity: l.quantity,
    prehistory_credit_quantity: l.prehistory_credit_quantity ?? '0',
    value_usd: l.value_usd,
    value_reason: l.value_reason,
    entry_price: l.entry_price ?? null,
    entry_price_reason: l.entry_price_reason ?? null,
    entry_price_asset: l.entry_price_asset ?? null,
    entry_price_kind: l.entry_price_kind ?? null,
    price_asset: l.price_asset ?? null,
    candle_price: l.price_level ?? null,
    candle_price_reason: l.price_level_reason ?? null,
    current_price: l.current_price ?? null,
    current_price_reason: l.current_price_reason ?? null,
    current_price_asset: l.current_price_asset ?? null,
    current_price_source: l.current_price_source ?? null,
    deviation_pct: l.deviation_pct ?? null,
    deviation_reason: l.deviation_reason ?? null,
    outside_amount: l.outside_quantity ?? null,
  };
}

export function toReceivable(r: W.ReceivableOut, now: Date = new Date()): Receivable {
  const pct = isZeroDecimal(r.sent) ? 0 : Math.max(0, Math.min(100, (Number(r.returned) / Number(r.sent)) * 100));
  return {
    deal_id: r.deal_id,
    deal_name: r.deal_name ?? `#${r.deal_id}`,
    location: r.location,
    asset: r.asset,
    sent: r.sent,
    returned: r.returned,
    outstanding: r.receivable,
    returned_pct: pct.toFixed(1),
    uncovered: r.uncovered,
    uncovered_reason: r.uncovered_reason,
    sent_at: r.sent_at ?? null,
    days_out: r.sent_at ? daysBetween(r.sent_at, now) : null,
  };
}

function assetsText(positions: readonly W.PositionOut[]): string {
  return positions.map((p) => `${formatDecimal(p.quantity)} ${p.asset}`).join(', ');
}

/** One bar segment per location of the deal (its positions grouped), widths = share of the deal total. */
export function toBreakdown(b: W.DealBreakdown): DealBreakdown {
  const byLoc = new Map<string, W.PositionOut[]>();
  for (const p of b.positions) byLoc.set(p.location, [...(byLoc.get(p.location) ?? []), p]);
  const total = sumUsd(b.positions);
  const segs: BreakdownSegment[] = [...byLoc.entries()].map(([location, ps]) => {
    const v = sumUsd(ps);
    const share =
      v.value !== null && total.value !== null && compareDecimal(total.value, '0') > 0 ? ((Number(v.value) / Number(total.value)) * 100).toFixed(1) : null;
    return { location, assets: assetsText(ps), value_usd: v.value, value_reason: v.reason, share_pct: share };
  });
  return {
    deal_id: b.deal_id,
    deal_name: b.deal_name,
    headline: total.value !== null ? `≈ ${formatDecimal(total.value, { dp: 0 })} USD` : '—',
    segments: segs,
  };
}

export function toOverview(o: W.Overview, now: Date = new Date()): Overview {
  const outside = new Set<string>();
  for (const b of o.deal_breakdown) for (const p of b.positions) if (p.location !== BINANCE_MARGIN) outside.add(p.location);
  return {
    kpis: {
      open_loan_usd: money(o.kpis.open_loan_usd),
      outside_binance_margin_usd: money(o.kpis.outside_binance_margin_usd),
      calculated_interest_usd: money(o.kpis.calculated_interest_usd),
      realized_result_usd: money(o.kpis.realized_result_usd),
      open_loan_count: o.open_loans.length,
      open_loan_prehistory_credit: toPrehistoryCredits(o.kpis.open_loan_prehistory_credit),
      closed_deal_count: o.deal_counts.closed,
      outside_locations: [...outside].sort(),
    },
    interest_gaps: o.interest_gaps,
    open_loans: o.open_loans.map(toOpenLoan),
    deal_breakdown: o.deal_breakdown.map(toBreakdown),
    receivables: o.receivables.map((r) => toReceivable(r, now)),
    recent_events: o.recent_events.map(toEventRow),
    valuation: { stablecoin_parity: o.valuation.stablecoin_parity, missing_price_assets: o.valuation.missing_price_assets, price_sources: { ...(o.valuation.price_sources ?? {}) } },
  };
}

export function toCandidate(c: W.CandidateOut): Candidate {
  return {
    external_id: c.external_id,
    occurred_at: c.occurred_at,
    type: c.type,
    symbol: c.symbol ?? c.asset,
    asset: c.asset,
    quantity: c.quantity,
    price: c.price,
    fee: c.fee_quantity,
    fee_asset: c.fee_asset,
    client_order_id: c.client_order_id,
    is_bot: c.is_bot,
    is_bot_reason: c.is_bot_reason,
    issues: c.issues,
    already_logged: c.already_logged,
    already_logged_voided: c.already_logged_voided ?? false,
    logged_deals: (c.logged_deals ?? []).map((a) => ({ deal_id: a.deal_id, deal_name: a.deal_name, quantity: a.quantity })),
  };
}

export function toCandidateList(l: W.CandidateList, account: string, now: Date = new Date()): CandidateList {
  return {
    from: l.window.from,
    to: l.window.to,
    account,
    fetched_at: now.toISOString(),
    items: l.items.map(toCandidate),
    notes: l.notes.map(candidateNoteText),
  };
}

export function toMarkOutcome(r: W.MarkResult): MarkOutcome {
  return { created: r.events.length, deal: { id: r.deal.id, name: r.deal.name }, skipped: r.skipped, merge_suggestions: r.merge_suggestions.length };
}

export function toMergeSuggestion(m: W.MergeSuggestionOut): MergeSuggestion {
  return { id: m.id, status: m.status, manual: toEventRow(m.manual_event), binance: toEventRow(m.binance_event), binance_detail: m.binance_event.external_id };
}

export function toCandlePrice(c: W.CandlePrice): CandlePrice {
  return { asset: c.asset, at: c.at, price: c.price, reason: c.reason, source: c.source };
}

export function toLoanSummary(loans: readonly W.Amount[] | undefined): string {
  if (!loans || loans.length === 0) return '—';
  return loans.map((l) => `${formatDecimal(l.quantity)} ${l.asset}`).join(' + ');
}

export function toDealSummary(d: W.DealSummary): DealSummary {
  return {
    id: d.id,
    name: d.name,
    status: d.status,
    reference_asset: d.reference_asset,
    tags: d.tags,
    opened_at: d.opened_at,
    closed_at: d.closed_at,
    loan_summary: toLoanSummary(d.open_loans),
    open_loan_prehistory_credit: toPrehistoryCredits(d.kpis.open_loan_prehistory_credit),
    outside_usd: d.kpis.outside_binance_margin_usd.value,
    outside_reason: d.kpis.outside_binance_margin_usd.reason ?? null,
    interest_usd: d.kpis.calculated_interest_usd.value,
    interest_reason: d.kpis.calculated_interest_usd.reason ?? null,
    result_usd: d.kpis.realized_result_usd.value,
    result_reason: d.kpis.realized_result_usd.reason ?? null,
  };
}

// ---- deal detail ---------------------------------------------------------
function toPositions(d: W.DealDetail): PositionRow[] {
  return d.positions.map((p) => {
    const cost = d.average_costs.find((c) => c.asset === p.asset);
    return {
      asset: p.asset,
      location: p.location,
      quantity: p.quantity,
      average_cost: cost?.average_price ?? null,
      average_cost_quote: cost?.quote_asset ?? null,
      average_cost_reason: cost ? null : 'not_applicable',
      value_usd: p.value_usd ?? null,
      value_reason: p.value_reason ?? null,
      cost_value_usd: p.cost_value_usd ?? null,
      cost_reason: p.cost_reason ?? null,
      ...unrealized(p.value_usd ?? null, p.value_reason ?? null, p.cost_value_usd ?? null, p.cost_reason ?? null),
    };
  });
}

/** Display-only difference (exact decimal arithmetic); unknown stays unknown with its reason. */
function unrealized(value: string | null, valueReason: string | null, cost: string | null, costReason: string | null) {
  if (value === null) return { unrealized_usd: null, unrealized_reason: valueReason ?? 'no_price' };
  if (cost === null) return { unrealized_usd: null, unrealized_reason: costReason ?? 'no_buys_allocated' };
  return { unrealized_usd: subDecimal(value, cost), unrealized_reason: null };
}

const nodeText = (parts: string[]) => parts.filter(Boolean).join(' · ');

/**
 * The flow diagram from the API's graph: borrow edges form the loan column,
 * every buy/sell/transfer/snapshot/result edge is one conversion box, every
 * asset location a location box (with its positions), repay edges the
 * repayment box. Prices come from the deal's events (an edge has none).
 */
export function buildFlow(d: W.DealDetail): FlowNodeView[] {
  const nodes = new Map(d.flow.nodes.map((n) => [n.id, n]));
  const events = new Map(d.events.map((e) => [e.id, e]));
  const out: FlowNodeView[] = [];
  const edges = [...d.flow.edges].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));

  const borrowed = new Map<string, string>();
  const repaid = new Map<string, string>();
  let firstBorrow: W.FlowEdge | null = null;
  for (const e of edges) {
    if (e.type === 'borrow') {
      borrowed.set(e.asset, addDecimal(borrowed.get(e.asset) ?? '0', e.quantity));
      firstBorrow ??= e;
    } else if (e.type === 'repay') {
      repaid.set(e.asset, addDecimal(repaid.get(e.asset) ?? '0', e.quantity));
    }
  }
  const qtyList = (m: Map<string, string>) => [...m.entries()].map(([a, q]) => `${formatDecimal(q)} ${a}`).join(' + ');
  if (borrowed.size > 0 && firstBorrow) {
    const home = nodes.get(firstBorrow.to_node)?.name ?? '';
    out.push({ id: 'loan', column: 'loan', emphasis: 'loan', label: 'Hitelfelvétel', title: qtyList(borrowed), detail: null, note: nodeText([home, formatUtc(firstBorrow.occurred_at)]) });
  }

  for (const e of edges) {
    const ev = events.get(e.event_id);
    const qty = `${formatDecimal(e.quantity)} ${e.asset}`;
    const at = formatUtc(e.occurred_at);
    const price = ev?.price ? `@ ${formatDecimal(ev.price)}${ev.quote_asset ? ` ${ev.quote_asset}` : ''}` : '';
    if (e.type === 'buy' || (e.type === 'liquidation' && ev?.side === 'buy')) {
      out.push({ id: `c-${e.event_id}`, column: 'conversion', emphasis: null, label: null, title: `Vétel ${qty}`, detail: nodeText([price, at]), note: null });
    } else if (e.type === 'sell' || e.type === 'liquidation') {
      out.push({ id: `c-${e.event_id}`, column: 'conversion', emphasis: null, label: null, title: `Eladás ${qty}`, detail: nodeText([price, at]), note: null });
    } else if (e.type === 'transfer') {
      const to = nodes.get(e.to_node)?.name ?? '';
      out.push({ id: `c-${e.event_id}`, column: 'conversion', emphasis: null, label: null, title: `Transzfer → ${to}`, detail: nodeText([qty, at]), note: null });
    } else if (e.type === 'balance_snapshot') {
      out.push({ id: `c-${e.event_id}`, column: 'conversion', emphasis: null, label: null, title: 'Egyenleg-rögzítés', detail: nodeText([qty, at]), note: null });
    } else if (e.type === 'result') {
      out.push({ id: `c-${e.event_id}`, column: 'conversion', emphasis: null, label: null, title: 'Eredmény', detail: nodeText([qty, at]), note: null });
    }
  }

  const touched = new Set<string>();
  for (const e of edges) {
    for (const id of [e.from_node, e.to_node]) if (nodes.get(id)?.kind === 'asset') touched.add(id);
  }
  for (const id of touched) {
    const n = nodes.get(id);
    if (!n) continue;
    const here = d.positions.filter((p) => p.location_id === id);
    const recv = d.receivables.find((r) => r.location_id === id && compareDecimal(r.receivable, '0') > 0);
    const usd = sumUsd(here);
    out.push({
      id: `l-${id}`,
      column: 'location',
      emphasis: recv ? 'out' : null,
      label: recv ? `${n.name} · visszahozandó` : n.name,
      title: here.length ? here.map((p) => `${formatDecimal(p.quantity)} ${p.asset}`).join(' · ') : 'nincs nyitott tétel',
      detail: here.length && usd.value !== null ? `ma ≈ ${formatDecimal(usd.value, { dp: 0 })} USD` : null,
      note: null,
    });
  }

  if (borrowed.size > 0) {
    const interest = d.kpis.calculated_interest_usd.value;
    out.push({
      id: 'repay',
      column: 'repayment',
      emphasis: 'dashed',
      label: 'Visszafizetés',
      title: [...borrowed.entries()].map(([a, q]) => `${formatDecimal(repaid.get(a) ?? '0')} / ${formatDecimal(q)} ${a}`).join(' + '),
      detail: null,
      note: interest !== null && interest !== undefined ? `+ ${formatDecimal(interest, { dp: 2 })} USD számolt kamat` : null,
    });
  }
  return out;
}

export function toDealDetail(d: W.DealDetail): DealDetail {
  const summary = toDealSummary({ ...d.deal, open_loans: d.open_loans, kpis: d.kpis });
  const loans = d.open_loans.map(toOpenLoan);
  const priced = loans.find((l) => l.entry_price !== null) ?? loans[0];
  const open = d.deal.status === 'open';
  const recvText = d.receivables
    .filter((r) => compareDecimal(r.receivable, '0') > 0)
    .map((r) => `${r.location} ${formatDecimal(r.receivable)} ${r.asset}`)
    .join(' · ');
  return {
    deal: { ...summary, note: d.deal.note },
    kpis: {
      open_loan: money(d.kpis.open_loan_usd),
      open_loan_caption: loans.length ? toLoanSummary(d.open_loans) : 'nincs nyitott hitel',
      open_loan_prehistory_credit: toPrehistoryCredits(d.kpis.open_loan_prehistory_credit),
      price: {
        entry: priced?.entry_price ?? null,
        entry_asset: priced?.entry_price_asset ?? null,
        reason: priced ? priced.entry_price_reason : 'no_open_loan',
        today: priced?.current_price ?? null,
        today_asset: priced?.current_price_asset ?? null,
        today_source: priced?.current_price_source ?? null,
        deviation_pct: priced?.deviation_pct ?? null,
      },
      outside: { ...money(d.kpis.outside_binance_margin_usd), breakdown: recvText || 'nincs kint lévő összeg' },
      interest: money(d.kpis.calculated_interest_usd),
      result: open
        ? { kind: 'unrealized', value: d.result.unrealized_usd ?? null, reason: d.result.unrealized_reason ?? 'no_price' }
        : { kind: 'realized', value: d.result.realized_usd, reason: d.result.realized_reason },
    },
    flow: buildFlow(d),
    positions: toPositions(d),
    receivables: d.receivables.map((r) => toReceivable(r)),
    events: d.events.map(toEventRow),
  };
}

// ---- locations -----------------------------------------------------------
export function toLocationItem(l: W.LocationOut): LocationItem {
  return { id: l.id, name: l.name, kind: l.kind, venue: l.venue, api_backed: l.api_backed, archived: l.archived };
}

export function toBalances(b: W.BalanceMatrix): Balances {
  const rows: BalanceRow[] = b.items.map((r) => ({
    ...r,
    value_usd: r.value_usd ?? null,
    value_reason: r.value_reason ?? null,
    cost_value_usd: r.cost_value_usd ?? null,
    cost_reason: r.cost_reason ?? null,
    returnable_outstanding: r.returnable_outstanding ?? null,
    returnable_asset: r.returnable_asset ?? null,
    last_movement_at: r.last_movement_at ?? null,
  }));
  const assetRows = rows.filter((r) => r.kind === 'asset');
  const totals = new Map<string, string>();
  for (const r of assetRows) {
    if (r.returnable_outstanding !== null && r.returnable_asset !== null) {
      totals.set(r.returnable_asset, addDecimal(totals.get(r.returnable_asset) ?? '0', r.returnable_outstanding));
    }
  }
  return {
    rows,
    total_usd: sumUsd(assetRows),
    total_cost_usd: sumUsd(assetRows.map((r) => ({ value_usd: r.cost_value_usd, value_reason: r.cost_reason }))),
    returnable_totals: [...totals].map(([asset, quantity]) => ({ asset, quantity })).sort((a, b) => a.asset.localeCompare(b.asset)),
  };
}

/** Location cards: the USD value of what the ledger holds there (asset rows of the balance matrix). */
export function summarizeLocations(items: readonly LocationItem[], balances: Balances): LocationSummary[] {
  return items.map((l) => {
    const rows = balances.rows.filter((r) => r.location_id === l.id && r.kind === 'asset');
    const assets = [...new Set(rows.map((r) => r.asset))];
    const deals = new Set(rows.filter((r) => r.deal_id !== null).map((r) => r.deal_id));
    const v = sumUsd(rows);
    const note = rows.length === 0 ? 'nincs nyitott tétel' : nodeText([deals.size ? `${deals.size} ügyletből` : '', assets.join(', ')]);
    return { ...l, value_usd: v.value, value_reason: v.reason, note };
  });
}
